import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useRef,
} from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { ArrowLeft } from '../../icons/lucide/ArrowLeft';
import { X } from '../../icons/lucide/X';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { Button } from '../Button';
import { GlassBackground } from '../Glass';
import { PaginationDots } from '../Pagination';
import { computePadding, NATIVE_SHEET_SHOWS_CLOSE } from './BottomSheet.layout';
import { BOTTOM_SHEET_NATIVE_SHADOW } from './BottomSheet.shadows';
import type { BottomSheetSurfaceProps } from './BottomSheet.types';
import {
  BOTTOM_SHEET_BORDER_WIDTH,
  BOTTOM_SHEET_GLASS_BLUR_PX,
  BOTTOM_SHEET_IMAGE_HEIGHT_PX,
  BOTTOM_SHEET_RADIUS_PX,
  bottomSheetBodyWrapClass,
  bottomSheetCardFillClass,
  bottomSheetCardNoFrostFillClass,
  bottomSheetContentVariants,
  bottomSheetFloatingCloseClass,
  bottomSheetFooterOverlayContentClass,
  bottomSheetFooterOverlayInFlowVariants,
  bottomSheetHandleClass,
  bottomSheetHeaderFloatingClosePadClass,
  bottomSheetHeaderVariants,
  bottomSheetHostedSurfaceVariants,
  bottomSheetImageCloseWrapClass,
  bottomSheetImageHeaderClass,
  bottomSheetMediaSlotClass,
  bottomSheetMediaSlotDottedClass,
  bottomSheetRegionPadClass,
  bottomSheetSubtitleClass,
  bottomSheetTitleClass,
  bottomSheetTitleGroupVariants,
  bottomSheetVariants,
  DEFAULT_BOTTOM_SHEET_STEP_DURATION_MS,
} from './BottomSheet.variants';
import { BottomSheetContentFade } from './BottomSheetContentFade';
import { BottomSheetDottedBackground } from './BottomSheetDottedBackground';
import { BottomSheetFooterBackground } from './BottomSheetFooterBackground';
import { BottomSheetFooterContent } from './BottomSheetFooterContent';
import { splitBottomSheetChildren } from './BottomSheetSlots';
import { BottomSheetSteps } from './BottomSheetSteps';

/** Icon sizes per Figma: back arrow 20, close ✕ 18. */
const BACK_ICON_SIZE = 20;
const CLOSE_ICON_SIZE = 18;

/**
 * The bottom sheet's presentational card — every variant Figma's "Bottom sheet"
 * component set (frame 5456:48693) specifies, and nothing else: no portal, no
 * backdrop, no gestures. {@link BottomSheet} wraps it with the platform shell.
 *
 * Use this directly only when something else already owns the presentation
 * (a Storybook frame gallery, an inline "sheet-styled" panel, a screenshot
 * baseline). For a real sheet, use {@link BottomSheet}.
 *
 * The header and footer variants are DERIVED, not enumerated — see
 * `BottomSheet.variants.ts` for the Figma-axis → prop mapping.
 *
 * @example
 * ```tsx
 * <BottomSheetSurface
 *   title="Delete deck?"
 *   subtitle="This can't be undone."
 *   closable
 *   onClose={dismiss}
 *   primaryAction={{ label: 'Delete', variant: 'alert', onPress: remove }}
 *   secondaryAction={{ label: 'Cancel', onPress: dismiss }}>
 *   <DeckPreview deck={deck} />
 * </BottomSheetSurface>
 * ```
 */
export function BottomSheetSurface({
  title,
  subtitle,
  image,
  imageHeight = BOTTOM_SHEET_IMAGE_HEIGHT_PX,
  closable = false,
  onClose,
  onBack,
  backAccessibilityLabel = 'Back',
  closeAccessibilityLabel = 'Close',
  align: alignProp = 'left',
  primaryAction,
  secondaryAction,
  footerLayout: footerLayoutProp = 'stacked',
  footerText,
  footerOverlay: footerOverlayProp = false,
  footerOverlap: footerOverlapProp,
  footerComponent,
  suppressFooter = false,
  showHandle = true,
  header = true,
  leftAction,
  rightAction,
  compact: compactProp = false,
  showHeaderBorder: showHeaderBorderProp = false,
  padding = 'default',
  contentScrolls = false,
  pagination,
  children,
  steps,
  step = 0,
  stepAnimationDuration = DEFAULT_BOTTOM_SHEET_STEP_DURATION_MS,
  disableAnimation,
  fill = false,
  bodyWrapper,
  footerWrapper,
  hostedHandleHeight,
  className,
  contentClassName,
  testID,
  webProps,
  nativeProps,
}: BottomSheetSurfaceProps) {
  const scale = useResponsiveScale();

  // The NATIVE shell hosts the card chrome in gorhom's own slots — the glass
  // card as `backgroundComponent`, the pill strip as a REAL `handleComponent`
  // (that's what makes the pill an actual drag surface). When it does, the
  // Surface renders as a bare column: no fill/border/shadow/blur, no pill,
  // no top inset (the strip provides it).
  const hosted = hostedHandleHeight != null;

  // Composable slots (`<BottomSheetTitle>`, `<BottomSheetActions>`, …) are
  // pulled out of `children` and win over the equivalent prop. They carry
  // CONTENT only — every region below is still drawn here, so both spellings
  // land on the same Figma geometry.
  const slots = splitBottomSheetChildren(children);

  const align = slots.header?.align ?? alignProp;
  const compact = slots.header?.compact ?? compactProp;
  const showHeaderBorder = slots.header?.showBorder ?? showHeaderBorderProp;

  const titleContent = slots.title ? slots.title.children : title;
  const subtitleContent = slots.description ? slots.description.children : subtitle;
  const imageContent = slots.image ? slots.image.children : image;
  // `'auto'` → no fixed height: the frame hugs a composed column (artwork +
  // caption text inside the media card).
  const slotImageHeight = slots.image?.height;
  const resolvedImageHeight =
    slotImageHeight === 'auto' ? undefined : (slotImageHeight ?? imageHeight);
  const hasBack = slots.back != null || onBack != null;
  // Hosted = the native shell, where sheets never show a ✕
  // (`NATIVE_SHEET_SHOWS_CLOSE`): both `closable` and a composed
  // `<BottomSheetClose>` are dropped there.
  const closeAllowed = !hosted || NATIVE_SHEET_SHOWS_CLOSE;
  // The slot and `closable` are alternatives, and the slot wins — otherwise a
  // composed ✕ and the floated one would both render.
  const hasCloseSlot = closeAllowed && slots.close != null;
  const showFloatingClose = closeAllowed && closable && !hasCloseSlot;

  const footerLayout = slots.actions?.layout ?? footerLayoutProp;
  const footerOverlay = slots.actions?.overlay ?? footerOverlayProp;
  // Dissolve by default only when height-constrained (`fill`); the slot/prop
  // opt-in forces it for hugging sheets whose artwork should bleed under the
  // actions (Figma 5662:11360).
  const footerOverlap = slots.actions?.overlap ?? footerOverlapProp ?? fill;
  const composedActions = slots.actions?.children;

  // Figma's `Header type` axis, derived from which slots the caller filled.
  const headerKind: 'image' | 'back' | 'title' | 'none' = imageContent
    ? 'image'
    : hasBack
      ? 'back'
      : titleContent != null
        ? 'title'
        : 'none';

  const hasFooterButtons =
    primaryAction != null || secondaryAction != null || composedActions != null;
  const hasFooterText = footerText != null && footerText !== false;
  const hasFooter = hasFooterButtons || hasFooterText || footerComponent != null;
  const hasInFlowFooter = hasFooter && !footerOverlay && !suppressFooter;

  // `padding` shapes the body only; the bottom clearance is the shell's job
  // (it owns the footer height + safe-area maths), so a bare Surface just
  // honours an explicit `padding={{ bottom }}`.
  const bodyPaddingStyle = computePadding(
    padding,
    typeof padding === 'object' && padding !== null ? (padding.bottom ?? 0) : 0,
  );

  /**
   * The standard ✕ — Figma's ghost 36px button, bevelled `secondary` over an
   * image header where a ghost glyph would vanish.
   */
  const renderCloseButton = (onPress?: BottomSheetSurfaceProps['onClose']) => (
    <Button
      variant={headerKind === 'image' ? 'secondary' : 'tertiary'}
      size="s"
      icon={X}
      iconSize={CLOSE_ICON_SIZE}
      onPress={onPress ?? onClose}
      accessibilityLabel={slots.close?.accessibilityLabel ?? closeAccessibilityLabel}
      testID={subTestID(testID, 'close')}
      className="shrink-0"
    />
  );

  /**
   * A composed `<BottomSheetClose>` sits in the header's trailing slot.
   * `asChild` swaps in the caller's own control, cloned with the dismiss
   * unless it brings its own `onPress` — the Radix convention.
   */
  const closeSlotNode = !hasCloseSlot
    ? null
    : slots.close?.asChild && isValidElement(slots.close.children)
      ? cloneElement(slots.close.children as ReactElement<{ onPress?: unknown }>, {
          onPress:
            (slots.close.children as ReactElement<{ onPress?: unknown }>).props.onPress ??
            slots.close.onPress ??
            onClose,
        })
      : renderCloseButton(slots.close?.onPress);

  const trailingSlot = closeSlotNode ?? rightAction ?? null;

  /** Figma's leading 40px bevelled arrow, or whatever the slot replaced it with. */
  const leadingSlot =
    slots.back?.children ??
    leftAction ??
    (hasBack ? (
      <Button
        variant="secondary"
        size="m"
        icon={ArrowLeft}
        iconSize={BACK_ICON_SIZE}
        onPress={slots.back?.onPress ?? onBack}
        accessibilityLabel={slots.back?.accessibilityLabel ?? backAccessibilityLabel}
        // A screen behind a sheet often has its own "Back"; the id is what tells
        // device automation the sheet's own apart (as `bottom-sheet-handle` does).
        // Literal first, then the sheet's own `<testID>-back` over it: the
        // literal is what flashka-app's selector audit (test-ids.spec) reads.
        testID="bottom-sheet-back"
        {...(testID ? { testID: subTestID(testID, 'back') } : null)}
        className="shrink-0"
      />
    ) : null);

  const titleGroup =
    titleContent == null && subtitleContent == null ? null : (
      <View className={bottomSheetTitleGroupVariants({ align })}>
        {/* `text-center` on the TEXTS, not only the group: RN has no CSS
            inheritance, so the group's own `text-center` never reaches the
            Text children on native. */}
        {renderText(
          titleContent,
          cn(
            bottomSheetTitleClass,
            align === 'center' && 'text-center',
            slots.title?.className,
          ),
          subTestID(testID, 'title'),
        )}
        {renderText(
          subtitleContent,
          cn(
            bottomSheetSubtitleClass,
            align === 'center' && 'text-center',
            slots.description?.className,
          ),
          subTestID(testID, 'subtitle'),
        )}
      </View>
    );

  // With a content slot, the surface card wraps the SLOT's children only —
  // loose siblings render after it (below the card), so a caller can put its
  // fields on the surface and its CTAs under it. Without a slot the loose
  // children ARE the body, as before.
  const rawBody = steps ? (
    <BottomSheetSteps
      steps={steps}
      step={step}
      duration={stepAnimationDuration}
      disableAnimation={disableAnimation}
    />
  ) : slots.content ? (
    slots.content.children
  ) : (
    slots.body
  );
  // `<BottomSheetContent>` draws Figma's "Media Slot" — a real bordered,
  // 28px-rounded translucent surface with 12px of padding, NOT a pass-through
  // (5443:44621; 5500:87000 shows it holding a menu). It goes INSIDE the
  // shells' body wrapper so the sheet's own `padding` still insets the card
  // from the sheet edge and the surface sits where the slot does.
  const isDotted = slots.content?.variant === 'dotted';

  // The surface is a STATIC box and the scroller lives INSIDE it — so its
  // border and all four corners stay put while only the content moves.
  //
  // It used to be the other way round (the shell's scroller wrapped the slot),
  // which meant the whole panel scrolled: its top and bottom edges slid out of
  // view, it never read as a bounded surface, and no amount of padding
  // arithmetic below it could fix that — the panel simply wasn't where the eye
  // expected it. Inverting the nesting is what makes the radius always visible;
  // the edge fades then say "there's more" where the border used to just
  // guillotine a row.
  const scrolledBody = bodyWrapper ? bodyWrapper(rawBody) : rawBody;
  const body = slots.content ? (
    <>
      <View
        className={cn(
          bottomSheetMediaSlotClass,
          // `min-h-0 flex-1` is what BOUNDS the inner scroller: without a bounded
          // height it would grow to its content and never scroll.
          fill && 'min-h-0 flex-1',
          'min-h-0 shrink',
          isDotted && bottomSheetMediaSlotDottedClass,
          slots.content.className,
        )}>
        {/* Behind the content, inside the slot's clip so the dots stop at the
            28px radius. */}
        {isDotted ? <BottomSheetDottedBackground /> : null}
        {scrolledBody}
        {contentScrolls ? (
          <>
            <BottomSheetContentFade edge="top" dotted={isDotted} />
            <BottomSheetContentFade edge="bottom" dotted={isDotted} />
          </>
        ) : null}
      </View>
      {/* Loose siblings BELOW the surface. Skipped for `steps` sheets, whose
          non-slot children have always been ignored. */}
      {steps ? null : slots.loose}
    </>
  ) : (
    scrolledBody
  );

  // Dev-only tripwire for the hugging-sheet trap (platform-traps.md): a body
  // whose root is `flex: 1` measures ZERO inside gorhom's content-sized
  // BottomSheetView, and the sheet opens as an empty pill. Bit twice during
  // the migration — warn once instead of shipping it a third time. Native
  // only by construction (`nativeProps` is dropped on web, where a flex child
  // of an auto-height column just sizes to its content).
  const warnedCollapsed = useRef(false);
  const warnIfBodyCollapsed =
    process.env.NODE_ENV !== 'production' && !fill
      ? (event: { nativeEvent: { layout: { height: number } } }) => {
          if (warnedCollapsed.current || event.nativeEvent.layout.height > 0) return;
          if (Children.toArray(slots.body).length === 0) return;
          warnedCollapsed.current = true;
          console.warn(
            '[BottomSheet] content-sized sheet body measured 0px tall — its root is probably `flex: 1` / `flex-1`; use a plain View (see mobile platform-traps.md).',
          );
        }
      : undefined;

  const footerContent = hasFooter ? (
    <BottomSheetFooterContent
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      footerLayout={footerLayout}
      footerText={footerText}
      footerComponent={footerComponent}
      actions={composedActions}
      testID={testID}
    />
  ) : null;

  const inFlowFooter = hasInFlowFooter ? footerContent : null;

  const mergedNativeProps = {
    ...(nativeProps ?? {}),
    style: [
      // Hosted: gorhom's backgroundComponent carries the card shadow.
      hosted ? undefined : BOTTOM_SHEET_NATIVE_SHADOW,
      (nativeProps as { style?: unknown } | undefined)?.style,
    ].filter(Boolean),
  };

  return (
    <View
      className={cn(
        hosted
          ? bottomSheetHostedSurfaceVariants({
              hasInFlowFooter,
              compact,
              bareTop: hostedHandleHeight === 0,
            })
          : bottomSheetVariants({ hasInFlowFooter, compact }),
        fill && 'h-full',
        // Shrinkable (inert while unbounded): a height-bounding host — the
        // keyboard shift's bound — compresses the BODY chain below (each hop
        // carries the same `min-h-0 shrink`) while the header and footer
        // rows, which have no shrink, keep their height.
        'min-h-0 shrink',
        className,
      )}
      testID={testID}
      webProps={webProps}
      nativeProps={mergedNativeProps}>
      {/* The card is `overflow-hidden` + `rounded-sheet`, so the native blur
          layer clips to the card's ACTUAL rendered corner: rem-scaled (the
          `rounded-sheet` token is rem, so a tablet / OS-large-text renders it
          up to 1.25× bigger) and inset by the 1px border, since RN lays an
          `inset-0` absolute child out against the padding box. Same maths as
          the Toast card. Hosted: the shell's backgroundComponent draws it. */}
      {hosted ? null : (
        <GlassBackground
          intensity={BOTTOM_SHEET_GLASS_BLUR_PX}
          radius={Math.max(0, BOTTOM_SHEET_RADIUS_PX * scale - BOTTOM_SHEET_BORDER_WIDTH)}
          fillClassName={bottomSheetCardFillClass}
          fallbackClassName={bottomSheetCardNoFrostFillClass}
          // A SELF-CHROMED surface is one rendered in place (a Storybook
          // frame, an inline "sheet-styled" panel) — i.e. inside the app's
          // `GlassBlurTarget`, so an Android frost here would sample the tree
          // it belongs to. Only the native shell's hosted card
          // (`CardChromeBackground`) sits outside it and may frost.
          frostOnAndroid={false}
          deferUntilSized
        />
      )}

      {/* Floated against the CARD, not the header, so it survives
          `header={false}` and a header with no title. Web shell only — the
          native shell never shows a ✕ (`showFloatingClose` is false there). */}
      {showFloatingClose ? (
        <View className={bottomSheetFloatingCloseClass}>{renderCloseButton()}</View>
      ) : null}

      {/* Hosted, the pill lives in gorhom's handleComponent — the strip that
          actually drags the sheet. Drawn here only for the self-chromed card
          (web shell, standalone galleries). */}
      {!hosted && header && showHandle ? (
        <View className={bottomSheetHandleClass} />
      ) : null}

      {!header ? null : headerKind === 'image' ? (
        <>
          <View
            className={bottomSheetImageHeaderClass}
            webProps={{ style: { height: resolvedImageHeight } }}
            nativeProps={{ style: { height: resolvedImageHeight } }}>
            {imageContent}
            {/* Bevelled over the artwork (Figma 5456:48556) — the ghost ✕ used
                in the text headers would vanish on a photo; `closeButton`
                already picks `secondary` for this header kind. */}
            {trailingSlot ? (
              <View className={bottomSheetImageCloseWrapClass}>{trailingSlot}</View>
            ) : null}
          </View>
          {titleGroup ? (
            <View
              className={cn(
                bottomSheetHeaderVariants({
                  kind: 'title',
                  align,
                  bordered: showHeaderBorder,
                }),
                showFloatingClose && bottomSheetHeaderFloatingClosePadClass,
              )}>
              {titleGroup}
            </View>
          ) : null}
        </>
      ) : headerKind === 'back' ? (
        <View
          className={cn(
            bottomSheetHeaderVariants({
              kind: 'back',
              align,
              bordered: showHeaderBorder,
            }),
            showFloatingClose && bottomSheetHeaderFloatingClosePadClass,
          )}>
          {leadingSlot}
          {titleGroup}
          {trailingSlot}
        </View>
      ) : headerKind === 'title' ? (
        <View
          className={cn(
            bottomSheetHeaderVariants({
              kind: 'title',
              align,
              bordered: showHeaderBorder,
            }),
            showFloatingClose && bottomSheetHeaderFloatingClosePadClass,
          )}>
          {leadingSlot}
          {titleGroup}
          {trailingSlot}
        </View>
      ) : null}

      <View
        className={cn(
          bottomSheetContentVariants({ hasInFlowFooter }),
          fill && 'min-h-0 flex-1',
          'min-h-0 shrink',
          contentClassName,
        )}>
        <View
          className={cn(
            bottomSheetBodyWrapClass,
            fill && 'min-h-0 flex-1',
            'min-h-0 shrink',
          )}>
          {/* `padding` shapes the BODY only — the header and footer keep
              Figma's 12px card inset regardless, so a full-bleed body
              (`padding="none"`) reaches the card edges without dragging the
              chrome with it. */}
          <View
            className={cn(
              'w-full',
              fill && 'min-h-0 flex-1',
              'min-h-0 shrink',
              // The DS's 12px gap AFTER the content surface, for loose
              // siblings rendered below the slot (an action bar, CTAs under a
              // fields card) — same rhythm as the card gap before it and the
              // in-flow footer gap. No-op when the slot is the only child.
              slots.content != null && 'gap-[12px]',
            )}
            webProps={{ style: bodyPaddingStyle }}
            nativeProps={{ style: bodyPaddingStyle, onLayout: warnIfBodyCollapsed }}>
            {body}
          </View>
          {pagination ? (
            <PaginationDots
              count={pagination.count}
              activeIndex={pagination.activeIndex}
              accessibilityLabel={pagination.accessibilityLabel}
              variant={pagination.variant}
            />
          ) : null}
        </View>
        {inFlowFooter ? (
          <View className={cn('w-full', bottomSheetRegionPadClass)}>
            {footerWrapper ? footerWrapper(inFlowFooter) : inFlowFooter}
          </View>
        ) : null}
      </View>

      {hasFooter && footerOverlay && !suppressFooter ? (
        // IN FLOW, not absolutely pinned: the footer then counts toward the
        // card's natural height, so a content-hugging sheet shows body and
        // actions stacked with no overlap. Only when the card is height-
        // constrained (`fill`) does the negative margin pull the footer over
        // the body's tail by FOOTER_OVERLAP_PX — the Figma dissolve, capped
        // at half the first button instead of swallowing the content.
        <View
          className={bottomSheetFooterOverlayInFlowVariants({ overlap: footerOverlap })}>
          {/* The blurred gradient backdrop hugs the BUTTON STACK (this inner
              box), not the full-width band — the band's padding stays clear. */}
          <View className={bottomSheetFooterOverlayContentClass}>
            <BottomSheetFooterBackground />
            {footerContent}
          </View>
        </View>
      ) : null}
    </View>
  );
}
BottomSheetSurface.displayName = 'BottomSheetSurface';

/**
 * String / number / array-of-those render inside a styled `<Text>` (required on
 * native). Any other JSX is rendered as-is — the caller owns its styling.
 */
function renderText(node: ReactNode, className: string, testID?: string): ReactNode {
  if (node == null || typeof node === 'boolean') return null;
  return isTextLike(node) ? (
    <Text className={className} testID={testID}>
      {node}
    </Text>
  ) : (
    node
  );
}

function isTextLike(node: unknown): boolean {
  if (node == null || typeof node === 'boolean') return false;
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.every(isTextLike);
  return false;
}
